import { apiClient } from './apiClient'
import { tokenService } from '../services/tokenService'
import { refreshTokenApi } from '../services/authService'
import { isTokenExpired } from '../utils/jwtUtils'

// Max concurrent requests allowed to wait for a token refresh
const MAX_REFRESH_WAITERS = 50

// Single shared promise while a refresh is in flight — all callers await the same one
let refreshPromise = null
let waiterCount = 0

// Opaque refresh tokens have no JWT structure, so skip local expiry check for them
const isJwt = token => token?.split('.').length === 3

// Dispatch a DOM event so the app can handle logout gracefully (e.g. redirect via router)
const emitLogout = () => {
  tokenService.clearTokens()
  window.dispatchEvent(new CustomEvent('auth:logout'))
}

const handleTokenRefresh = async () => {
  const refreshToken = tokenService.getRefreshToken()

  if (!refreshToken || (isJwt(refreshToken) && isTokenExpired(refreshToken))) {
    throw new Error('Refresh token missing or expired')
  }

  const data = await refreshTokenApi(refreshToken)

  // Validate before writing — avoids clobbering a valid refresh token on a bad response
  if (!data?.token || !data?.refresh_token) {
    throw new Error('Invalid token response from server')
  }

  tokenService.setTokens({
    accessToken: data.token,
    refreshToken: data.refresh_token,
  })

  return data.token
}

// Returns the in-flight refresh promise if one exists, or starts a new one.
// All concurrent callers share the same promise, preventing double-refresh.
const getRefreshedToken = () => {
  if (waiterCount >= MAX_REFRESH_WAITERS) {
    return Promise.reject(new Error('Too many requests awaiting token refresh'))
  }

  waiterCount++

  if (!refreshPromise) {
    refreshPromise = handleTokenRefresh().finally(() => {
      refreshPromise = null
      waiterCount = 0
    })
  }

  return refreshPromise
}

// REQUEST — proactively refresh before the access token expires
apiClient.interceptors.request.use(
  async config => {
    const token = tokenService.getAccessToken()
    if (!token) return config

    if (isTokenExpired(token)) {
      try {
        const newToken = await getRefreshedToken()
        config.headers.Authorization = `Bearer ${newToken}`
      } catch (err) {
        console.error('[Auth] Token refresh failed:', err.message)
        emitLogout()
        return Promise.reject(err)
      }
    } else {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  },
  error => Promise.reject(error)
)

// RESPONSE — reactive fallback for unexpected 401s that slip through
apiClient.interceptors.response.use(
  res => res,
  async error => {
    const originalRequest = error.config

    if (!error.response) return Promise.reject(error)
    if (error.response.status !== 401 || originalRequest._retry) {
      return Promise.reject(error)
    }

    originalRequest._retry = true

    try {
      const newToken = await getRefreshedToken()
      originalRequest.headers.Authorization = `Bearer ${newToken}`
      return apiClient(originalRequest)
    } catch (err) {
      console.error('[Auth] Token refresh failed on 401:', err.message)
      emitLogout()
      return Promise.reject(err)
    } finally {
      // Clean up so a later legitimate retry isn't permanently blocked
      delete originalRequest._retry
    }
  }
)
